import { typographyCss } from '@creditornot/cb-ingredients';
import { getCookieName } from '@creditornot/cwc-cookie-sdk';
import { FORM_ERROR } from 'final-form';
import React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { useCookie } from 'react-use';
import styled from 'styled-components';

import { patchInboundMerchant } from 'apis/merchant-onboarding-service/api';
import { Typography as T } from 'common/typography';

import { MOBILE_BREAKPOINT } from '../../../../common/hooks/useIsMobile';
import { useRegisterTelemetryEvent } from '../../../../core/telemetry/hooks/useRegisterTelemetryEvent';
import { useCookieConsents } from '../../../cookie-consents/CookieConsentsProvider';
import { CountryConfig } from '../../SelfServeFormView/SelfServeSignUpForm/types';
import { SelfServeStepsIdentifiers } from '../constants';
import BusinessDetailsForm from '../forms/BusinessDetailsForm/BusinessDetailsForm';
import { businessDetailsFromApi, businessDetailsToApi } from '../forms/transformer';
import { BusinessDetails, BusinessDetailsValidationSchema } from '../forms/validation';
import { validateStepParam } from '../helpers';
import { InboundMerchant } from '../types';

type BusinessInformationProps = {
  inboundMerchant: InboundMerchant;
  countriesConfigs: CountryConfig[];
  goToNextStep: () => void;
  setUpdateIsInProgress: (inProgress: boolean) => void;
};

const Wrapper = styled.div``;

const ContentContainer = styled.div`
  margin-block-end: 32px;
`;

const Title = styled(T.Heading6)`
  margin-block-start: 32px;
  padding-block-end: 16px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Title1()}
  }
`;

const BusinessInformation = ({
  inboundMerchant,
  countriesConfigs,
  goToNextStep,
  setUpdateIsInProgress,
}: BusinessInformationProps) => {
  const { formatMessage } = useIntl();
  const { search } = useLocation();
  const register = useRegisterTelemetryEvent();
  const {
    consents: { analytics: analyticsConsent, marketing: marketingConsent },
  } = useCookieConsents();
  const [woltUid] = useCookie(getCookieName('wolt-uid'));
  const { country } = inboundMerchant;

  const searchParams = new URLSearchParams(search);

  const handleBusinessDetailsSubmit = async (
    values: BusinessDetails,
  ): Promise<{ [key: string]: string | Error } | void> => {
    try {
      setUpdateIsInProgress(true);
      const validFormValues = BusinessDetailsValidationSchema.validateSync(values);
      const transformedValues = businessDetailsToApi(validFormValues);
      transformedValues.venues![0].openingTimes = inboundMerchant.venues[0].openingTimes;
      const stepParam = validateStepParam(searchParams.get('step'));

      register({
        event: 'business_details_submitted',
        marketingConsent,
        analyticsConsent,
        properties: {
          __woltUid: woltUid,
          country,
        },
      });

      await patchInboundMerchant({ ...transformedValues, ssmoStepCompletionId: stepParam }, inboundMerchant.merchantId);
      goToNextStep();
    } catch (submitError) {
      return { [FORM_ERROR]: submitError as Error };
    } finally {
      setUpdateIsInProgress(false);
    }
  };

  return (
    <Wrapper>
      <ContentContainer>
        <Title>{formatMessage({ id: 'self-serve-sign-up.wizard.review-info.business-details' })}</Title>
        <BusinessDetailsForm
          country={inboundMerchant.country}
          initialData={businessDetailsFromApi(inboundMerchant)}
          countryConfigs={countriesConfigs}
          handleSubmit={handleBusinessDetailsSubmit}
          formId={SelfServeStepsIdentifiers.BusinessDetails}
          disabled
        />
      </ContentContainer>
    </Wrapper>
  );
};

export default BusinessInformation;
